import * as R from 'ramda';
import { reduceObj } from '../../../../main/support/utils';
import type { GroupByResponse, Query } from '../../../../main/types/druidTypes';

export type GroupByResponses = {
  [key: string]: GroupByResponse;
};

type BucketTotals = {
  [key: string]: {
    [key: string]: string;
    total: string;
  };
};

/**
 * given an array of filter names and an object that maps dimensions to filters,
 * returns the dimensions that correspond to those filters.
 * For Example:
 *
 * filterValues - ['Facebook', 'Taptica', 'ANDROID']
 * filters -
 * {
    last_attributed_touch_data_advertising_partner_name: ['Facebook', 'Taptica'],
    user_data_os: ['IOS']
 * }
 * returns ['last_attributed_touch_data_advertising_partner_name']
 */
export const getDimensionsFromFilterValues = (filterValues: string[], filters: Record<string, any>): string[] => {
  const dimensions = Object.keys(filters);
  return filterValues.reduce((acc, val) => {
    const dimension = dimensions.reduce((prev, dim) => {
      if (R.contains(val, filters[dim])) {
        return dim;
      }
      return prev;
    }, '');

    if (dimension && !R.contains(dimension, acc)) {
      return acc.concat(dimension as any);
    }

    return acc;
  }, []);
};

/**
 * given a number span and interval, return an array of numbers between the span spaced at the interval.
 * For Example:
 *
 * span - [0, 15]
 * interval - 5
 * returns [0, 10, 15].
 */
export const generateDistributionTickValues = (span: number[], interval: number): number[] => {
  let domain = span.slice(0, 1);
  while (domain[domain.length - 1] + interval <= span[1]) {
    domain = domain.concat(domain[domain.length - 1] + interval);
  }
  return domain;
};

/**
 * method that returns the numeric bucket that a given value falls into
 *
 * For Example:
 * val - 3
 * range - [0, 5, 10]
 * returns 5
 */
export const getBucket = (val: number, range: number[]): number => {
  if (typeof val !== 'number') {
    val = parseInt(val);
  }
  const first = range[0];
  const last = range[range.length - 1];

  if (val < first || val > last) {
    return -1;
  }

  // return val if it is already one of the buckets
  if (range.includes(val)) {
    return val;
  }

  return R.reduceWhile(
    R.isNil,
    (_, curr) => {
      if (val <= curr) {
        return curr;
      }
      return null;
    },
    null,
    range
  );
};

/**
 * method that zero fills the y values of react viz data.
 */
export const zeroFillDistributions = (ticks: number[], filters: string[]): Record<string, any> => {
  return filters.reduce((acc, filter) => {
    const series = ticks.map((t) => ({ x: t, y: 0 }));
    return R.merge(acc, {
      [filter]: {
        data: series,
      },
    });
  }, {});
};

/**
 * method that divides each property value of some object by some total value.
 */
export const deriveRatios = (acc: Record<string, any>, val: Record<string, any>, key: string): Record<string, any> => {
  const ratios = R.map((n) => n / val.total, val);
  delete ratios.total;
  return R.assoc(key, ratios, acc);
};

/**
 * method that derives distribution ratios by calling deriveRatios on each property in an object.
 */
export const deriveDistributionRatios = (map: BucketTotals): Record<string, any> => {
  return reduceObj(deriveRatios, {}, map);
};

/**
 * given a zero filled object of react vis data and and object of distribution ratios, generate a new object to be consumed by react vis for graphing.
 */
export const hydrateDistributions = (
  zeroFilled: Record<string, any>,
  filterToRatios: Record<string, any>
): Record<string, any> => {
  return R.mapObjIndexed((val, key) => {
    const hydrate = val.data || [];
    const hydrated = hydrate.map((d) => {
      const ratio = R.pathOr(0, [key, d.x], filterToRatios);
      return R.merge(d, { y: ratio });
    });
    return R.assoc('data', hydrated, val);
  }, zeroFilled);
};

/**
 * given an array of druid events, generate the total metric count in each tick value bucket.
 * final output maps these bucketed totals to filters
 */
export const generateFilterToBucketMap = (
  data: Record<string, any>[],
  filters: string[],
  dimensions: string[],
  ticks: number[],
  domain: string,
  metric: string
): Record<string, any> => {
  return dimensions.reduce((acc, dimension) => {
    const curr = data.reduce((filterMap, datum) => {
      const { event = {} } = datum;
      const filter = R.find(R.equals(event[dimension]), filters);
      if (!filter) {
        return filterMap;
      }

      const bucket = getBucket(datum.event[domain], ticks);

      const count = datum.event[metric];
      const bucketLens = R.lensPath([filter, bucket]);
      const totalLens = R.lensPath([filter, 'total']);
      const prevBucketCount = R.view(bucketLens, filterMap) || 0;
      const prevTotalCount = R.view(totalLens, filterMap) || 0;

      if (bucket < 0) {
        return R.set(totalLens, prevTotalCount + count, filterMap);
      }
      // update filterMap[filter][bucket] and filterMap[filter][total]
      return R.pipe(R.set(bucketLens, prevBucketCount + count), R.set(totalLens, prevTotalCount + count))(filterMap);
    }, {});

    return R.merge(acc, curr);
  }, {});
};

/**
 * given an array of druid events, generate the total metric count in each tick value bucket.
 * input assumes events are already mapped to their corresponding filters.
 * For Example:
 *
 * data -
 * {
    'all paid advertising': [
      {
        event: {...},
        timestamp: '04-05-2018T00:00:00Z',
        version: v1
 *   },
    ....
    ]
 * }
 */
export const generateBucketMap = (
  data: Record<string, any>,
  ticks: number[],
  domain: string,
  metric: string
): Record<string, any> => {
  return R.map((val) => {
    return val.reduce((acc, datum) => {
      const { event = {} } = datum;

      if (R.isNil(event[domain])) {
        return acc;
      }
      const bucket = getBucket(event[domain], ticks);

      const count = datum.event[metric];
      const prevBucketCount = acc[bucket] || 0;
      const prevTotalCount = acc.total || 0;

      // we want to include all events in the denominator even if they do not fall within the desired range
      if (bucket < 0) {
        return R.assoc('total', prevTotalCount + count, acc);
      }

      return R.pipe(R.assoc(bucket, prevBucketCount + count), R.assoc('total', prevTotalCount + count))(acc);
    }, {});
  }, data);
};

/**
 * method that returns true if all the y values for a given line are zero and false otherwise.
 */
export const isZeroFilled = (line: Record<string, any>): boolean => {
  if (!line.data) {
    return true;
  }
  return line.data.reduce((acc, point) => {
    if (point.y > 0) {
      return false;
    }
    return acc;
  }, true);
};

/**
 * method that removes lines that are completely zero filled (y values are all zero).
 */
export const trimEmptyLines = (lines: Record<string, any>) => {
  const zeroFilled = Object.keys(lines).filter((line) => isZeroFilled(lines[line]));
  return R.omit(zeroFilled, lines);
};

/**
 * method that takes in druid events, desired tick values corresponding to distribution buckets and returns react vis formatted data mapped to each filter.
 */
export const processDistributionDataWithFilters = (
  druidData: GroupByResponse,
  query: Query,
  filters: string[],
  dimensions: string[],
  ticks: number[],
  domain: string,
  metric: string
): Record<string, any> | null => {
  const baseline = Object.keys(druidData)[0];
  const dataSource = R.path(['data_sources', 0], query);

  let data;
  if (!baseline) {
    data = [];
  } else if (baseline !== dataSource) {
    data = druidData[baseline][dataSource];
  } else {
    data = druidData[dataSource];
  }

  if (!data || data.length < 0) {
    return null;
  }

  const filtersToBuckets = generateFilterToBucketMap(data, filters, dimensions, ticks, domain, metric);

  const filterToRatios = deriveDistributionRatios(filtersToBuckets);

  const zeroFilled = zeroFillDistributions(ticks, filters);

  const hydrated = hydrateDistributions(zeroFilled, filterToRatios);

  return trimEmptyLines(hydrated);
};

/**
 * method that takes in druid events, desired tick values corresponding to distribution buckets and returns react vis formatted data mapped to each filter.
 * NOTE: This method differs from processDistributionDataWithFilters since the incoming druid data is already mapped to filters.
 */
export const processDistributionData = (
  druidDataObject: GroupByResponses,
  query: Query,
  ticks: number[],
  domain: string,
  metric: string
): Record<string, any> | null => {
  const dataSource = R.path(['data_sources', 0], query);
  const data = R.map((b) => b[dataSource], druidDataObject);

  if (R.find(R.isNil, data)) {
    return null;
  }

  const toBuckets = generateBucketMap(data, ticks, domain, metric);

  const ratios = deriveDistributionRatios(toBuckets);

  const zeroFilled = zeroFillDistributions(ticks, Object.keys(data));

  const hydrated = hydrateDistributions(zeroFilled, ratios);

  return trimEmptyLines(hydrated);
};
